import type { RecommendationStatus } from '../../types/recommendationGraph';

/** Persisted roadmap workflow states (stored on graph nodes + Firestore). */
export const RECOMMENDATION_STATUS_ORDER: RecommendationStatus[] = [
  'not-started',
  'initiated',
  'pending-feedback',
  'blocked',
  'completed',
];

const LEGACY_STATUS_MAP: Record<string, RecommendationStatus> = {
  'in-progress': 'initiated',
  complete: 'completed',
};

export function isRecommendationStatus(value: string): value is RecommendationStatus {
  return (RECOMMENDATION_STATUS_ORDER as string[]).includes(value);
}

/** Normalize legacy persisted values after status vocabulary change. */
export function normalizeRecommendationStatus(
  status: string | undefined,
): RecommendationStatus {
  if (!status) return 'not-started';
  if (isRecommendationStatus(status)) return status;
  return LEGACY_STATUS_MAP[status] ?? 'not-started';
}

/**
 * Roadmap execution progress across a node set — the shared form of the
 * inline `statusBreakdown` ribbon math in RecommendationGraphView, reused by
 * the Path-to-GO roadmap side-quest so both surfaces report the same
 * completed/total.
 */
export function roadmapProgress(
  nodes: ReadonlyArray<{ status?: string }>,
): { completed: number; total: number } {
  let completed = 0;
  for (const n of nodes) {
    if (normalizeRecommendationStatus(n.status) === 'completed') completed += 1;
  }
  return { completed, total: nodes.length };
}

export function recommendationStatusLabel(status: RecommendationStatus): string {
  switch (status) {
    case 'not-started':
      return 'Not started';
    case 'initiated':
      return 'Initiated';
    case 'pending-feedback':
      return 'Pending feedback';
    case 'blocked':
      return 'Blocked';
    case 'completed':
      return 'Completed';
    default:
      return status;
  }
}

// Aurora SUBTLE status tints flip dark/light: in dark theme `*-subtle` is a
// dark wash and `text-*` is the bright accent (readable); in light theme
// `*-subtle` is a pale wash and `text-*` is the medium accent (readable). This
// fixes the previous light-only emerald/sky/violet/amber washes that left card
// text unreadable in dark mode. Bands stay distinct: completed=success,
// initiated=info, pending-feedback=conditional-high, blocked=danger,
// not-started=neutral surface.

/** Full-card surface tint (badge uses the stronger variant in the view). */
export function recommendationStatusCardClass(status: RecommendationStatus): string {
  switch (status) {
    case 'completed':
      return 'bg-success-subtle border-success/40 hover:border-success/60';
    case 'initiated':
      return 'bg-info-subtle border-info/40 hover:border-info/60';
    case 'pending-feedback':
      return 'bg-conditional-high-subtle border-conditional-high/40 hover:border-conditional-high/60';
    case 'blocked':
      return 'bg-danger-subtle border-danger/40 hover:border-danger/60';
    default:
      return 'bg-background border-border hover:border-primary/40';
  }
}

export function recommendationStatusBadgeClass(status: RecommendationStatus): string {
  switch (status) {
    case 'completed':
      return 'bg-success-subtle text-success border-success/50';
    case 'initiated':
      return 'bg-info-subtle text-info border-info/50';
    case 'pending-feedback':
      return 'bg-conditional-high-subtle text-conditional-high border-conditional-high/50';
    case 'blocked':
      return 'bg-danger-subtle text-danger border-danger/50';
    default:
      return 'bg-surface-1 text-text-secondary border-border';
  }
}

export function normalizeRecommendationGraphStatuses<
  T extends { nodes: Array<{ status: string }> },
>(graph: T): T {
  return {
    ...graph,
    nodes: graph.nodes.map((n) => ({
      ...n,
      status: normalizeRecommendationStatus(n.status),
    })),
  } as T;
}
